/**
 * Argus Live — React Query hooks (AL-B3).
 *
 * Wraps the AL-A2 CRUD API (`packages/api/src/routes/v1/argus-live/`):
 *
 *   - useArgusLiveDashboards()         GET    /argus-live/dashboards
 *   - useArgusLiveDashboard(id)        GET    /argus-live/dashboards/:id
 *   - useCreateArgusLiveDashboard()    POST   /argus-live/dashboards
 *   - useUpdateArgusLiveDashboard()    PATCH  /argus-live/dashboards/:id
 *   - useArgusLiveVersions(id)         GET    /argus-live/dashboards/:id/versions
 *   - useArgusLiveVersion(id, vId)     GET    /argus-live/dashboards/:id/versions/:versionId
 *   - useCreateArgusLiveVersion()      POST   /argus-live/dashboards/:id/versions
 *   - usePublishArgusLiveDashboard()   POST   /argus-live/dashboards/:id/publish
 *   - useArchiveArgusLiveDashboard()   POST   /argus-live/dashboards/:id/archive
 *   - useUnarchiveArgusLiveDashboard() POST   /argus-live/dashboards/:id/unarchive
 *   - useDeleteArgusLiveDashboard()    DELETE /argus-live/dashboards/:id
 *
 * Wire shapes match the Zod schemas declared in
 * `packages/api/src/routes/v1/argus-live/dashboards.ts` +
 * `packages/api/src/routes/v1/argus-live/versions.ts` (see DashboardSchema,
 * VersionSchema, DashboardWithVersionSchema).
 *
 * Query-key strategy mirrors the rest of the platform (cf.
 * `use-tenant-system-library.ts`) — a top-level `argus-live` namespace
 * with sub-keys per entity + id so a mutation can invalidate just the
 * slice it changed.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { apiClient } from './api-client';

// ─── Wire shapes ─────────────────────────────────────────────────────────

export interface ArgusLiveClassification {
  sensitivity: Sensitivity;
  exportControl: ExportControl;
}

export interface ArgusLiveDashboard {
  id: string;
  tenantId: string;
  ownerUserId: string;
  slug: string;
  title: string;
  description: string | null;
  icon: string | null;
  status: 'draft' | 'published' | 'archived';
  enabled: boolean;
  currentVersionId: string | null;
  schemaVersion: number;
  classificationTag: ArgusLiveClassification;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  archivedAt: string | null;
}

export interface ArgusLiveVersion {
  id: string;
  tenantId: string;
  dashboardId: string;
  revLabel: string;
  revSequence: number;
  codeHtml: string;
  codeJs: string;
  codeCss: string;
  dataBindings: unknown[];
  sandboxConfig: Record<string, unknown>;
  classificationTag: ArgusLiveClassification;
  contentHash: string;
  authoredByUserId: string;
  authoredViaAgentId: string | null;
  authoredViaConversationId: string | null;
  architectReview: unknown | null;
  createdAt: string;
}

/**
 * Per-dashboard effective grant role returned by the dashboard GET route.
 *
 * Ordered low → high: view < operator < edit < admin. `operator` (Cockpit
 * Phase 2) = view + (Phase 3) execute/approve gated actions, but NOT edit
 * layout. Matches the API `DashboardWithVersionSchema.effectiveRole` enum in
 * `packages/api/src/routes/v1/argus-live/dashboards.ts`.
 */
export type ArgusLiveEffectiveRole = 'view' | 'operator' | 'edit' | 'admin';

export interface ArgusLiveDashboardWithVersion {
  dashboard: ArgusLiveDashboard;
  currentVersion: ArgusLiveVersion | null;
  /** Caller's effective grant role on this dashboard (null = no grant). */
  effectiveRole: ArgusLiveEffectiveRole | null;
}

// ─── Query keys ──────────────────────────────────────────────────────────

export const argusLiveKeys = {
  all: ['argus-live'] as const,
  dashboards: (filter?: { status?: string; includeArchived?: boolean }) =>
    ['argus-live', 'dashboards', filter ?? {}] as const,
  dashboard: (id: string) => ['argus-live', 'dashboard', id] as const,
  versions: (id: string) => ['argus-live', 'versions', id] as const,
  version: (id: string, versionId: string) =>
    ['argus-live', 'version', id, versionId] as const,
};

// ─── List dashboards ─────────────────────────────────────────────────────

export interface UseArgusLiveDashboardsParams {
  status?: 'draft' | 'published' | 'archived';
  includeArchived?: boolean;
}

export function useArgusLiveDashboards(
  params: UseArgusLiveDashboardsParams = {},
  options?: Omit<
    UseQueryOptions<{ data: ArgusLiveDashboard[] }, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.includeArchived === true) qs.set('includeArchived', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<{ data: ArgusLiveDashboard[] }, Error>({
    queryKey: argusLiveKeys.dashboards(params),
    queryFn: () =>
      apiClient.get<{ data: ArgusLiveDashboard[] }>(
        `/argus-live/dashboards${suffix}`,
      ),
    ...options,
  });
}

// ─── Single dashboard (with current version) ─────────────────────────────

export function useArgusLiveDashboard(
  id: string | null | undefined,
  options?: Omit<
    UseQueryOptions<{ data: ArgusLiveDashboardWithVersion }, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<{ data: ArgusLiveDashboardWithVersion }, Error>({
    queryKey: id ? argusLiveKeys.dashboard(id) : ['argus-live', '__skip__'],
    queryFn: () =>
      apiClient.get<{ data: ArgusLiveDashboardWithVersion }>(
        `/argus-live/dashboards/${id}`,
      ),
    enabled: !!id,
    ...options,
  });
}

// ─── Versions list / get ─────────────────────────────────────────────────

export function useArgusLiveVersions(id: string | null | undefined) {
  return useQuery<{ data: ArgusLiveVersion[] }, Error>({
    queryKey: id ? argusLiveKeys.versions(id) : ['argus-live', '__skip__'],
    queryFn: () =>
      apiClient.get<{ data: ArgusLiveVersion[] }>(
        `/argus-live/dashboards/${id}/versions`,
      ),
    enabled: !!id,
  });
}

export function useArgusLiveVersion(
  dashboardId: string | null | undefined,
  versionId: string | null | undefined,
) {
  return useQuery<{ data: ArgusLiveVersion }, Error>({
    queryKey:
      dashboardId && versionId
        ? argusLiveKeys.version(dashboardId, versionId)
        : ['argus-live', '__skip__'],
    queryFn: () =>
      apiClient.get<{ data: ArgusLiveVersion }>(
        `/argus-live/dashboards/${dashboardId}/versions/${versionId}`,
      ),
    enabled: !!dashboardId && !!versionId,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────

export interface CreateDashboardInput {
  slug: string;
  title: string;
  description?: string | null;
  icon?: string | null;
}

export function useCreateArgusLiveDashboard() {
  const qc = useQueryClient();
  return useMutation<
    { data: ArgusLiveDashboard },
    Error,
    CreateDashboardInput
  >({
    mutationFn: (body) =>
      apiClient.post<{ data: ArgusLiveDashboard }>(
        '/argus-live/dashboards',
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: argusLiveKeys.all });
    },
  });
}

export interface UpdateDashboardInput {
  id: string;
  title?: string;
  description?: string | null;
  icon?: string | null;
}

export function useUpdateArgusLiveDashboard() {
  const qc = useQueryClient();
  return useMutation<
    { data: ArgusLiveDashboard },
    Error,
    UpdateDashboardInput
  >({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch<{ data: ArgusLiveDashboard }>(
        `/argus-live/dashboards/${id}`,
        body,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboard(vars.id) });
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboards() });
    },
  });
}

export interface CreateVersionInput {
  dashboardId: string;
  revLabel: string;
  codeHtml: string;
  codeJs: string;
  codeCss: string;
  dataBindings?: unknown[];
  sandboxConfig?: Record<string, unknown>;
  authoredViaConversationId?: string;
}

export function useCreateArgusLiveVersion() {
  const qc = useQueryClient();
  return useMutation<{ data: ArgusLiveVersion }, Error, CreateVersionInput>({
    mutationFn: ({ dashboardId, ...body }) =>
      apiClient.post<{ data: ArgusLiveVersion }>(
        `/argus-live/dashboards/${dashboardId}/versions`,
        body,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: argusLiveKeys.versions(vars.dashboardId) });
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboard(vars.dashboardId) });
    },
  });
}

// ─── AI authoring (Cockpit Phase 6 — POST /dashboards/:id/author) ─────────
//
// Wire shapes mirror the strict() Zod schemas in
// `packages/api/src/routes/v1/argus-live/author.ts`:
//   POST /argus-live/dashboards/:id/author → 201 AuthoredVersionSchema
//   422 → { error: { code: 'AUTHORING_HANDOFF'|'AUTHORING_UNAVAILABLE', ...,
//                    details: { attempts?, errors?, lastCandidate? } } }
// On a 201 the route has ALREADY persisted a new version
// (sandbox_config_jsonb.cockpitSpec); the editor loads `spec` for review/edit
// then runs the normal save/publish flow.

import type { CockpitArchetypeSlug } from '@argusai/shared';
import type { ExportControl, Sensitivity } from '@argusai/shared';

export interface AuthorCockpitInput {
  dashboardId: string;
  /** Natural-language description of the cockpit to build. */
  prompt: string;
  /** Optional archetype scaffold to seed/edit from. */
  archetype?: CockpitArchetypeSlug;
  /** Optional explicit binding/action manifest the spec may reference. */
  availableBindingIds?: string[];
  availableActionIds?: string[];
  /** Optional rev label for the new version. */
  revLabel?: string;
}

export interface AuthorCockpitResult {
  versionId: string;
  revSequence: number;
  revLabel: string;
  /** The validated CockpitSpec that was authored + persisted. */
  spec: unknown;
  attempts: number;
  fromArchetype: boolean;
  latencyMs: number;
}

/**
 * Structured authoring failure surfaced from the route's 422 body. The editor
 * shows these honestly (the re-author loop's per-node errors) so the user can
 * refine the prompt or finish in the editor — never a silent "it didn't work".
 */
export interface AuthorCockpitError extends Error {
  code?: string;
  attempts?: number;
  errors?: Array<{ code: string; message: string; path: string }>;
  lastCandidate?: unknown;
}

/**
 * Mutation hook for AI-authoring a CockpitSpec. On success the new version is
 * already persisted server-side; the caller loads `result.spec` into the
 * editor. On failure it throws an {@link AuthorCockpitError} carrying the
 * structured re-author errors + the last best-effort candidate.
 */
export function useAuthorCockpit() {
  const qc = useQueryClient();
  return useMutation<AuthorCockpitResult, AuthorCockpitError, AuthorCockpitInput>({
    mutationFn: async ({ dashboardId, ...body }) => {
      // /author is SSE (text/event-stream): authoring runs minutes on the APU, so
      // a blocking POST 504s at Caddy's 30s timeout. metadata + 15s heartbeats keep
      // the stream alive; exactly one terminal `final` (success) or `error` frame.
      // The 403/404 gates reply JSON BEFORE the stream opens. Mirrors the resident.
      const res = await apiClient.requestStream(
        `/argus-live/dashboards/${dashboardId}/author`,
        { method: 'POST', bodyJson: body },
      );
      if (!res.ok || !res.body) {
        // Pre-stream gate error (JSON 403/404) or transport failure.
        const text = await res.text().catch(() => '');
        let parsed: { error?: { code?: string; message?: string } } | null = null;
        try {
          parsed = JSON.parse(text);
        } catch {
          /* non-JSON */
        }
        const authorErr = new Error(
          parsed?.error?.message || `Authoring failed: HTTP ${res.status}`,
        ) as AuthorCockpitError;
        if (parsed?.error?.code) authorErr.code = parsed.error.code;
        throw authorErr;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let final: AuthorCockpitResult | null = null;
      let errorFrame: { code?: string; message?: string; details?: Record<string, unknown> } | null =
        null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
          if (!frame.startsWith('data:')) continue;
          const json = frame.slice(5).trim();
          if (!json) continue;
          let payload: {
            type?: string;
            data?: AuthorCockpitResult;
            code?: string;
            message?: string;
            details?: Record<string, unknown>;
          };
          try {
            payload = JSON.parse(json);
          } catch {
            continue;
          }
          if (payload.type === 'final' && payload.data) {
            final = payload.data;
          } else if (payload.type === 'error') {
            errorFrame = {
              code: payload.code,
              message: payload.message,
              details: payload.details,
            };
          }
          // metadata / heartbeat / done are not surfaced to the consumer.
        }
      }

      if (final) return final;
      // A terminal `error` frame (or a stream that ended without `final`).
      const authorErr = new Error(
        errorFrame?.message || 'Authoring did not produce a cockpit.',
      ) as AuthorCockpitError;
      if (errorFrame?.code) authorErr.code = errorFrame.code;
      const details = errorFrame?.details;
      if (details) {
        if (typeof details.attempts === 'number') authorErr.attempts = details.attempts;
        if (Array.isArray(details.errors)) {
          authorErr.errors = details.errors as AuthorCockpitError['errors'];
        }
        if ('lastCandidate' in details) authorErr.lastCandidate = details.lastCandidate;
      }
      throw authorErr;
    },
    onSuccess: (_d, vars) => {
      // A new version landed — refresh the version list + the dashboard row.
      qc.invalidateQueries({ queryKey: argusLiveKeys.versions(vars.dashboardId) });
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboard(vars.dashboardId) });
    },
  });
}

export interface PublishDashboardInput {
  dashboardId: string;
  existingVersionId?: string;
  revLabel?: string;
  codeHtml?: string;
  codeJs?: string;
  codeCss?: string;
  dataBindings?: unknown[];
  sandboxConfig?: Record<string, unknown>;
  authoredViaConversationId?: string;
}

export function usePublishArgusLiveDashboard() {
  const qc = useQueryClient();
  return useMutation<
    { data: { dashboard: ArgusLiveDashboard; version: ArgusLiveVersion } },
    Error,
    PublishDashboardInput
  >({
    mutationFn: ({ dashboardId, ...body }) =>
      apiClient.post(`/argus-live/dashboards/${dashboardId}/publish`, body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboard(vars.dashboardId) });
      qc.invalidateQueries({ queryKey: argusLiveKeys.versions(vars.dashboardId) });
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboards() });
    },
  });
}

export function useArchiveArgusLiveDashboard() {
  const qc = useQueryClient();
  return useMutation<{ data: ArgusLiveDashboard }, Error, { dashboardId: string }>({
    mutationFn: ({ dashboardId }) =>
      apiClient.post(`/argus-live/dashboards/${dashboardId}/archive`, {}),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboard(vars.dashboardId) });
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboards() });
    },
  });
}

export function useUnarchiveArgusLiveDashboard() {
  const qc = useQueryClient();
  return useMutation<{ data: ArgusLiveDashboard }, Error, { dashboardId: string }>({
    mutationFn: ({ dashboardId }) =>
      apiClient.post(`/argus-live/dashboards/${dashboardId}/unarchive`, {}),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboard(vars.dashboardId) });
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboards() });
    },
  });
}

export function useDeleteArgusLiveDashboard() {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, { dashboardId: string }>({
    mutationFn: ({ dashboardId }) =>
      apiClient.delete(`/argus-live/dashboards/${dashboardId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: argusLiveKeys.all });
    },
  });
}

// ─── Grants (AL-A2 + AL-C5) ──────────────────────────────────────────────

/**
 * Wire shape for a `tenant_dashboard_grants` row.
 *
 * Matches the response schema in
 * `packages/api/src/routes/v1/argus-live/grants.ts` — `GrantSchema`.
 *
 * Invariant (enforced server-side by the XOR check): exactly one of
 * `granteeUserId`, `granteeGroupId`, or `granteeRole` is non-null.
 */
export interface ArgusLiveGrant {
  id: string;
  tenantId: string;
  dashboardId: string;
  granteeUserId: string | null;
  granteeGroupId: string | null;
  granteeRole: string | null;
  role: 'view' | 'edit' | 'admin';
  grantedByUserId: string;
  grantedAt: string;
  expiresAt: string | null;
}

/** Role labels accepted by the POST /grants endpoint's `granteeRole`. */
export type ArgusLiveGranteeRole = 'owner' | 'admin' | 'member' | 'viewer';

/**
 * Grant role tiers accepted by the POST endpoint's `role`. Ordered low →
 * high: view < operator < edit < admin. `operator` (Cockpit Phase 2) = view +
 * (Phase 3) execute/approve, but NOT edit layout. Matches the API
 * CreateGrantBody + DASHBOARD_GRANT_ROLES.
 */
export type ArgusLiveGrantRole = 'view' | 'operator' | 'edit' | 'admin';

export const argusLiveGrantsKey = (dashboardId: string) =>
  ['argus-live', 'grants', dashboardId] as const;

export function useArgusLiveGrants(
  dashboardId: string | null | undefined,
  options?: Omit<
    UseQueryOptions<{ data: ArgusLiveGrant[] }, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<{ data: ArgusLiveGrant[] }, Error>({
    queryKey: dashboardId
      ? argusLiveGrantsKey(dashboardId)
      : ['argus-live', '__skip__'],
    queryFn: () =>
      apiClient.get<{ data: ArgusLiveGrant[] }>(
        `/argus-live/dashboards/${dashboardId}/grants`,
      ),
    enabled: !!dashboardId,
    ...options,
  });
}

export interface CreateGrantInput {
  dashboardId: string;
  granteeUserId?: string;
  granteeGroupId?: string;
  granteeRole?: ArgusLiveGranteeRole;
  role: ArgusLiveGrantRole;
  /** Optional ISO-8601 datetime when the grant should expire. */
  expiresAt?: string;
}

export function useCreateArgusLiveGrant() {
  const qc = useQueryClient();
  return useMutation<{ data: ArgusLiveGrant }, Error, CreateGrantInput>({
    mutationFn: ({ dashboardId, ...body }) =>
      apiClient.post<{ data: ArgusLiveGrant }>(
        `/argus-live/dashboards/${dashboardId}/grants`,
        body,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: argusLiveGrantsKey(vars.dashboardId) });
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboard(vars.dashboardId) });
    },
  });
}

export interface DeleteGrantInput {
  dashboardId: string;
  grantId: string;
}

export function useDeleteArgusLiveGrant() {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, DeleteGrantInput>({
    mutationFn: ({ dashboardId, grantId }) =>
      apiClient.delete(
        `/argus-live/dashboards/${dashboardId}/grants/${grantId}`,
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: argusLiveGrantsKey(vars.dashboardId) });
      qc.invalidateQueries({ queryKey: argusLiveKeys.dashboard(vars.dashboardId) });
    },
  });
}

// ─── Resident copilot (Cockpit Phase 5 — operate view) ───────────────────
//
// Wire shapes mirror the strict() Zod schemas in
// `packages/api/src/routes/v1/argus-live/resident.ts`:
//   POST /argus-live/dashboards/:id/resident/chat   → ChatSuccessSchema
//   GET  /argus-live/dashboards/:id/resident/chips  → ChipsSuccessSchema
//   POST /argus-live/dashboards/:id/resident/chips/:chipId/dismiss
//                                                    → DismissSuccessSchema

/** A proposed (approval-gated) write the resident surfaces — never auto-run. */
export interface ResidentProposedAction {
  approvalId: string;
  actionId: string;
  kind: 'mcp_tool' | 'workflow';
  summary: string;
}

export interface ResidentQueuedWorkflow {
  workflowId: string;
  executionId: string;
  deduped: boolean;
}

export interface ResidentChatResponse {
  ok: true;
  answer: string;
  proposedActions: ResidentProposedAction[];
  queuedWorkflows: ResidentQueuedWorkflow[];
  effort: 'low' | 'medium' | 'high';
  contextTokens: number;
  latencyMs: number;
}

/** A proactive chip (≤3). Click is a pure pre-fill / anchor — mints NO authz. */
export type ResidentChipClick =
  | { kind: 'composer_query'; query: string }
  | { kind: 'row_anchor'; tableSlug: string; rowKey: string }
  | { kind: 'open_approval'; approvalId: string };

export interface ResidentChip {
  id: string;
  kind: 'workflow_completed' | 'high_priority_row' | 'approval_needs_you';
  label: string;
  click: ResidentChipClick;
  at: string;
}

export interface ResidentChipsResponse {
  ok: true;
  chips: ResidentChip[];
  proactiveEnabled: boolean;
}

export const argusLiveResidentChipsKey = (dashboardId: string) =>
  ['argus-live', 'resident-chips', dashboardId] as const;

/**
 * Poll the resident proactive chips. The cockpit operate view refetches on a
 * gentle interval so a completed workflow / new high-priority row surfaces
 * without a manual reload. Disabled when the caller has no dashboard id.
 */
export function useArgusLiveResidentChips(
  dashboardId: string | null | undefined,
  options?: Omit<
    UseQueryOptions<ResidentChipsResponse, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ResidentChipsResponse, Error>({
    queryKey: dashboardId
      ? argusLiveResidentChipsKey(dashboardId)
      : ['argus-live', '__skip__'],
    queryFn: () =>
      apiClient.get<ResidentChipsResponse>(
        `/argus-live/dashboards/${dashboardId}/resident/chips`,
      ),
    enabled: !!dashboardId,
    // The chips are advisory; a slow refresh keeps the panel fresh without
    // hammering the resident-chip engine.
    refetchInterval: 30_000,
    ...options,
  });
}

export interface ResidentChatInput {
  dashboardId: string;
  message: string;
  sessionId?: string;
}

/**
 * The settled result of a resident chat turn, assembled from the SSE stream's
 * terminal `final` event. Mirrors the pre-streaming `ResidentChatResponse`
 * shape so callers that only care about the final answer keep working.
 */
export interface ResidentChatStreamResult {
  answer: string;
  proposedActions: ResidentProposedAction[];
  queuedWorkflows: ResidentQueuedWorkflow[];
  effort: 'low' | 'medium' | 'high';
  contextTokens: number;
  latencyMs: number;
}

export interface UseArgusLiveResidentChatResult {
  /** True from `send()` until the SSE stream terminates. */
  isStreaming: boolean;
  /** Incremental assistant text — grows as tokens arrive (typing effect). */
  streamingText: string;
  /** Terminal error message (HTTP failure or an `error` SSE frame), or null. */
  error: string | null;
  /**
   * Submit a turn. Streams tokens into `streamingText`; resolves with the
   * settled `final` payload (answer + proposed actions + queued workflows).
   * Rejects on a pre-stream HTTP failure or an `error` SSE frame.
   */
  send: (input: ResidentChatInput) => Promise<ResidentChatStreamResult>;
  /** Abort the in-flight stream. Idempotent. */
  abort: () => void;
}

/**
 * SSE-consuming resident chat hook (Cockpit Phase 5, Rule #24).
 *
 * Replaces the prior blocking `apiClient.post` mutation: the resident endpoint
 * now streams (`text/event-stream`) because gpt-oss-20b at forced-high effort
 * runs >30s before the first byte — a blocking JSON reply 504'd at caddy.
 * Mirrors `use-studio-helper.ts` / `use-chat-stream.ts`: it uses
 * `apiClient.requestStream` (which carries the bearer + 401-refresh), reads the
 * `data:`-framed body, accumulates `token` deltas into `streamingText`, and
 * resolves on the terminal `final` event.
 *
 * SSE protocol (mirrors the route):
 *   { type: 'metadata', effort, contextTokens, traceId }
 *   { type: 'token',    content }
 *   { type: 'final',    answer, proposedActions, queuedWorkflows, effort,
 *                       contextTokens, latencyMs, sessionId? }
 *   { type: 'error',    code, message }
 *   { type: 'heartbeat' } | { type: 'done' }
 */
export function useArgusLiveResidentChat(): UseArgusLiveResidentChatResult {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const send = useCallback(
    async ({
      dashboardId,
      message,
      sessionId,
    }: ResidentChatInput): Promise<ResidentChatStreamResult> => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setError(null);
      setStreamingText('');
      setIsStreaming(true);

      let final: ResidentChatStreamResult | null = null;
      let streamErrorMessage: string | null = null;

      try {
        const res = await apiClient.requestStream(
          `/argus-live/dashboards/${dashboardId}/resident/chat`,
          {
            method: 'POST',
            bodyJson: { message, ...(sessionId ? { sessionId } : {}) },
            signal: ac.signal,
          },
        );

        if (!res.ok || !res.body) {
          const text = await res.text().catch(() => '');
          let msg = `Resident chat failed: HTTP ${res.status}`;
          try {
            const parsed = JSON.parse(text) as {
              error?: { message?: string };
            };
            if (parsed?.error?.message) msg = parsed.error.message;
          } catch {
            /* non-JSON body — keep the status message */
          }
          throw new Error(msg);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';
          for (const frame of frames) {
            if (!frame.startsWith('data:')) continue;
            const json = frame.slice(5).trim();
            if (!json) continue;
            let payload: {
              type?: string;
              content?: string;
              answer?: string;
              proposedActions?: ResidentProposedAction[];
              queuedWorkflows?: ResidentQueuedWorkflow[];
              effort?: 'low' | 'medium' | 'high';
              contextTokens?: number;
              latencyMs?: number;
              code?: string;
              message?: string;
            };
            try {
              payload = JSON.parse(json);
            } catch {
              continue;
            }
            if (payload.type === 'token' && typeof payload.content === 'string') {
              const delta = payload.content;
              setStreamingText((prev) => prev + delta);
            } else if (payload.type === 'final') {
              final = {
                answer: payload.answer ?? '',
                proposedActions: payload.proposedActions ?? [],
                queuedWorkflows: payload.queuedWorkflows ?? [],
                effort: payload.effort ?? 'high',
                contextTokens: payload.contextTokens ?? 0,
                latencyMs: payload.latencyMs ?? 0,
              };
            } else if (payload.type === 'error') {
              streamErrorMessage =
                payload.message ?? payload.code ?? 'The resident failed.';
            }
            // metadata / heartbeat / done are not surfaced to the consumer.
          }
        }
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') {
          // User-aborted — surface a typed error the caller can ignore.
          setIsStreaming(false);
          throw err;
        }
        const msg = (err as Error).message || 'The resident failed.';
        setError(msg);
        setIsStreaming(false);
        throw new Error(msg);
      }

      setIsStreaming(false);

      if (streamErrorMessage) {
        setError(streamErrorMessage);
        throw new Error(streamErrorMessage);
      }
      if (!final) {
        const msg = 'The resident stream ended without an answer.';
        setError(msg);
        throw new Error(msg);
      }
      return final;
    },
    [],
  );

  return { isStreaming, streamingText, error, send, abort };
}

export interface DismissChipInput {
  dashboardId: string;
  chipId: string;
}

export function useDismissArgusLiveResidentChip() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; dismissedKinds: string[] },
    Error,
    DismissChipInput
  >({
    mutationFn: ({ dashboardId, chipId }) =>
      apiClient.post(
        `/argus-live/dashboards/${dashboardId}/resident/chips/${encodeURIComponent(
          chipId,
        )}/dismiss`,
        {},
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: argusLiveResidentChipsKey(vars.dashboardId),
      });
    },
  });
}
